import { ConversationChannelTypeExtended } from '@provision/chat-utils';
import {
  ChannelIdentifiers,
  ConversationChannelType,
  ConversationParticipantEntity,
  ConversationParticipantOption,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { ConversationMessage, EntityIdentifier } from '@provision/entity-types';
import { Address, PlatformUser } from 'src/graphql';
import {
  ClientRaw,
  ConversationAssigneeGroupRaw,
  ConversationAttachmentRaw,
  ConversationMessageRaw,
  ConversationRaw,
  ConversationSummaryRaw,
  PartnerRaw,
} from 'src/types';

export type Participants = Record<string, ConversationParticipant>;

export type ConversationParticipant = ClientRaw | PlatformUser;

export type ConversationAttachment = Pick<
  ConversationAttachmentRaw,
  | 'id'
  | 'conversationAttachmentId'
  | 'conversationId'
  | 'partnerId'
  | 'fileKey'
  | 'fileName'
  | 'createdAt'
> & {
  contentType?: string | null;
  size?: number | null;
};

export type ConversationMessageData = Pick<
  ConversationMessageRaw,
  'partnerId' | 'createdAt'
> & {
  id: string;
  senderEntity?: ConversationParticipantEntity;
  message: string;
};

export type Conversation = Pick<
  ConversationRaw,
  | 'conversationId'
  | 'workspaceConversationId'
  | 'subject'
  | 'createdAt'
  | 'conversationAssigneeGroupId'
  | 'requester'
  | 'associatedAddresses'
  | 'conversationStatusId'
  | 'conversationStatusUpdatedAt'
  | 'lastMessageReceivedAt'
  | 'requesterId'
  | 'snoozeUntil'
  | 'assignee'
  | 'partnerId'
  | 'labelAssignments'
  | 'summaries'
> & {
  id: string;
  type: ConversationType;
  urgency: string;
  partner: PartnerRaw;
  friendlyId: string;
  attachments: ConversationAttachment[];
  conversationAssigneeGroup?: ConversationAssigneeGroupRaw | null;
  conversationAssigneeId?: string | null;
  brandGroups?: { supportEmail?: string }[];
  requesterFullName?: string;
  assigneeFullName?: string;
  unseenCount?: number;
  requesterData?: ConversationParticipantOption;
  address?: Address;
  isDeleted?: boolean;
  latestSummary?: ConversationSummaryRaw | null;
  latestSummaryText?: string | null;
  // raw custom_attributes blob, present on grid rows so custom attribute columns decode from
  // the same cache field the side panel editor reads/writes.
  customAttributes?: string | null;
};

export interface MessageGroup {
  id: string;
  createdAt: string;
  label?: string;
  channelType: ConversationChannelTypeExtended;
  messages: ConversationMessageData[];
  senderEntity: ConversationParticipantEntity;
  sender?: ConversationParticipant;
  senderId: string;
}

export enum ConversationType {
  SUPPORT_TICKET = 'support_ticket',
  MEMBER_SERVICES = 'member_services',
  NON_SUPPORT_EVENT = 'non_support_event',
}

export enum ConversationChatApp {
  CLIENT_GATEWAY = 'client_gateway',
  PORTAL = 'portal',
}

export interface FileInput {
  url: string;
  size: number;
  name: string;
  type: string;
}

export type MessageData = Pick<ConversationMessage, 'isPrivate' | 'body'> & {
  smsSessionId?: string;
  conversationMessageId?: string;
  attachments?: FileInput[];
  mentions?: EntityIdentifier[];
  /** Makes the message a reply to this internal note. */
  parentConversationMessageId?: string;
};

export type ConversationData = Pick<
  Conversation,
  | 'conversationId'
  | 'subject'
  | 'type'
  | 'urgency'
  | 'partnerId'
  | 'workspaceConversationId'
  | 'requester'
  | 'conversationStatusId'
  | 'snoozeUntil'
> & { attachments?: FileInput[] };

export interface SubmitConversationDataChannel {
  externalIdentifier: string;
  internalIdentifier: string;
  channelType: ConversationChannelType;
  useToSendMessage?: boolean;
  participationMode?: ConversationParticipationMode;
}

export interface SubmitConversationDataParticipant {
  entity: ConversationParticipantEntity;
  identity: string;
  channels: SubmitConversationDataChannel[];
  isRequester?: boolean;
  entityPartnerId?: number;
  name?: string;
}
export interface IgnoreActiveChannelsInfo {
  participationMode: ConversationParticipationMode;
  channelType?: ConversationChannelType;
}

export interface ConversationDataForSubmission {
  conversation: Partial<ConversationData>;
  message: MessageData;
  activeChannels?: ChannelIdentifiers[];
  ignoreActiveChannelsFor: IgnoreActiveChannelsInfo[];
  macroActions?: string;
  participants: SubmitConversationDataParticipant[];
}

export enum ConversationMessageDispatchStatus {
  Dispatching = 'dispatching',
  Dispatched = 'dispatched',
  Failed = 'failed',
  Seen = 'seen',
}
